import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { stableDeepEqual } from "@/utils/stableDeepEqual";
import { logDiscardConfirmation } from "@/utils/discardConfirmationLog";
import {
  tUnsavedChanges,
  type UnsavedChangesGuardStringKey,
} from "@/i18n/unsavedChangesStrings";

/**
 * Guards a dialog/form against accidental data loss.
 * - Tracks "dirty" state via a snapshot baseline you provide.
 * - Intercepts close attempts when dirty and surfaces a confirm prompt.
 * - Warns on browser tab close / refresh while dirty.
 *
 * Equality is computed via {@link stableDeepEqual} so that key ordering and
 * empty/undefined entries (common in PPPC choice maps and config KVPs) do not
 * cause false-positive dirty states.
 */
export function useUnsavedChangesGuard<T>(params: {
  open: boolean;
  current: T;
  baseline: T;
  onClose: () => void;
  /**
   * Optional callback invoked when the user confirms "Discard" in the
   * unsaved-changes prompt. Use this to revert the form's local state to
   * the saved baseline immediately, before (or instead of) closing.
   */
  onDiscard?: () => void;
  /**
   * Optional label identifying which dialog/form this guard protects (e.g.
   * "PrivacyPermissionsDialog"). Used for client-side discard analytics so
   * we can later trace how often users revert changes per surface.
   */
  surface?: string;
  /**
   * Optional per-call i18n overrides. Useful for surface-specific copy
   * ("Discard PPPC changes?") or when previewing a non-default locale.
   * Keys you omit fall back to the global registry, then to English.
   */
  i18n?: Partial<Record<UnsavedChangesGuardStringKey, string>>;
  /**
   * When true, the discard flow is locked: close attempts are ignored, the
   * confirm prompt won't open, and an already-open prompt disables its
   * "Discard" action. Wire this to your save mutation's `isPending` so users
   * can't blow away in-flight edits and end up with inconsistent state.
   */
  isSaving?: boolean;
}) {
  const {
    open,
    current,
    baseline,
    onClose,
    onDiscard,
    surface,
    i18n,
    isSaving = false,
  } = params;
  const [confirmOpen, setConfirmOpen] = useState(false);
  const baselineRef = useRef(baseline);

  // Re-snapshot baseline whenever the dialog (re)opens.
  useEffect(() => {
    if (open) baselineRef.current = baseline;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const isDirty = open && !stableDeepEqual(current, baselineRef.current);

  // Warn on tab close / refresh while dirty.
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  const requestClose = useCallback(
    (nextOpen: boolean) => {
      if (nextOpen) return; // opening — never blocked
      // While a save is in flight we refuse to close — neither a clean exit
      // nor opening the discard prompt — to keep state consistent.
      if (isSaving) return;
      if (isDirty) {
        setConfirmOpen(true);
        return;
      }
      onClose();
    },
    [isDirty, isSaving, onClose],
  );

  const confirmDiscard = useCallback(() => {
    // Belt-and-braces: even if the dialog button slips through, never
    // discard while a save is mid-flight.
    if (isSaving) return;
    setConfirmOpen(false);
    // Revert local form state to the saved baseline before closing so any
    // immediate reopen (or in-place dialog) shows a clean state.
    onDiscard?.();
    onClose();
    toast.success(tUnsavedChanges("discardToast", i18n, surface));
    logDiscardConfirmation({ surface: surface ?? "unknown" });
  }, [isSaving, onClose, onDiscard, surface, i18n]);

  const cancelDiscard = useCallback(() => setConfirmOpen(false), []);

  // Reset baseline after a successful save so the dialog can close cleanly.
  // Also dismiss any open discard confirmation (and suppress its toast) since
  // the user's changes were just persisted — discarding is no longer relevant.
  const markClean = useCallback(() => {
    baselineRef.current = current;
    setConfirmOpen(false);
  }, [current]);

  return {
    isDirty,
    requestClose,
    confirmOpen,
    confirmDiscard,
    cancelDiscard,
    markClean,
    /** Mirrored back so consumers can disable the dialog's Discard button. */
    isSaving,
    /**
     * Per-call i18n overrides forwarded from params. Spread onto
     * `<UnsavedChangesDialog />` so its title, description, and button
     * labels resolve through the same registry as the discard toast.
     */
    i18n,
    /**
     * Surface label forwarded from params. Pass to the dialog so it can
     * resolve registered surface presets (see
     * `registerUnsavedChangesSurfacePreset`).
     */
    surface,
  };
}
